import {
  faClone,
  faCoins,
  faEdit,
  faEllipsisV,
  faPlus,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useContext, useMemo, useState } from 'react';

import {
  DraggableItem,
  DraggrableContainer,
  ItemDraggableType,
} from '../../../../components/draggable';
import './styles.scss';
import {
  OTC_TRADER_TABS,
  OTC_TRADER_TABS_KEYS,
  OTCTraderContext,
  TAB_PREFIX,
} from '../../../../providers/otc-trader/otc-trader-provider';
import { getTokenPairName } from '../../../../providers/otc-trader/quick-pricer-provider';
import Utils from '../../../../utils/immersive_utils';
import HandleTabModal, { TypeOfView } from '../../components/handle-tab-modal';

export const OTCTraderTabsBase = () => {
  const {
    currentTab,
    setCurrentTab,
    tabs: tabsRaw,
    addTab,
    deleteTab,
    renameTab,
    duplicateTab,
    setTabs,
  } = useContext(OTCTraderContext);

  const tabs = useMemo(() => {
    return tabsRaw.map((tab) => {
      const finded = OTC_TRADER_TABS_KEYS.find((item) => item.key === tab.key);

      return finded || tab;
    });
  }, [tabsRaw]);

  const [modalType, setModalType] = useState(TypeOfView.NEW_TAB);
  const [openHandleTabModal, setOpenHandleTabModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleTabChange = (newTab: OTC_TRADER_TABS) => {
    setCurrentTab(newTab);
  };

  const handleCloseModal = () => {
    setOpenHandleTabModal(false);
  };

  const handleAddTab = (label: string, tokenPair: string) => {
    const key = `${TAB_PREFIX}${tokenPair}_${label}`;
    addTab(key, label);
  };

  const handleRenameTab = (newTab: string) => {
    renameTab(currentTab, newTab);
  };

  const handleDuplicateTab = (newTab: string) => {
    duplicateTab(newTab);
  };

  const handleDeleteTab = () => {
    deleteTab(currentTab);
  };

  const renderTab = useMemo(() => {
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    const renderMenu = () => {
      const handleClickOnOption = (event: React.MouseEvent<HTMLElement>, type: TypeOfView) => {
        event.stopPropagation();
        setModalType(type);
        setOpenHandleTabModal(true);
        handleClose();
      };
      return (
        <Menu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          <MenuItem onClick={(e) => handleClickOnOption(e, TypeOfView.RENAME_TAB)}>
            <ListItemIcon>
              <FontAwesomeIcon icon={faEdit} />
            </ListItemIcon>
            <ListItemText>Rename tab</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => handleClickOnOption(e, TypeOfView.DUPLICATE_TAB)}
            className="menu-item"
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faClone} />
            </ListItemIcon>
            <ListItemText>Duplicate tab</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={(e) => handleClickOnOption(e, TypeOfView.DELETE_TAB)}
            className="menu-item"
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={faTrash} color="#D82C0D" />
            </ListItemIcon>
            <ListItemText sx={{ color: '#D82C0D' }}>Delete tab</ListItemText>
          </MenuItem>
        </Menu>
      );
    };

    const handleUpdateList = (data: ItemDraggableType[]) => {
      setTabs(
        data.map((d: any) => {
          return {
            key: d?.id,
            label: d?.label,
          };
        }),
      );
    };

    const itemList = tabs.map((tab) => ({ id: tab.key, ...tab }));

    return (
      <DraggrableContainer items={itemList} onUpdate={handleUpdateList}>
        {itemList?.map((tab: any) => {
          const isSelected = currentTab === tab.key;
          const isCustomTab = !!tab.key.startsWith(TAB_PREFIX);

          const isSurfaceMarking = OTC_TRADER_TABS.SURFACE_MARKING === tab.key;

          const renderIcon = () => {
            if (isSurfaceMarking) return null;

            const defaultIcon = <FontAwesomeIcon icon={faCoins} className="icon" />;

            if (isCustomTab) {
              const token = getTokenPairName(tab.key)?.split('_').length
                ? getTokenPairName(tab.key)?.split('_')[0]
                : null;

              const src = Utils.getTokenIconPathBySign(token);
              const alt = Utils.getTokenNameBySign(token);

              if (!!src && !src.includes('undefined')) {
                return <img src={src} className="icon" alt={alt} />;
              }
            }

            return defaultIcon;
          };

          const spanClassName = isSurfaceMarking ? 'otc-tab-surface-marking' : '';

          return (
            <DraggableItem key={tab.key} id={tab.id}>
              <div
                key={tab.key}
                className={`otc-trader-tab ${isSelected ? 'otc-tab-selected' : ''}`}
                onClick={() => handleTabChange(tab.key)}
              >
                <div className="otc-trader-tabs-content">
                  {renderIcon()}
                  <span className={spanClassName}>{tab.label}</span>
                  {isSelected && isCustomTab && (
                    <div onClick={handleClick} className="otc-tab-button">
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </div>
                  )}
                  {renderMenu()}
                </div>
              </div>
            </DraggableItem>
          );
        })}
      </DraggrableContainer>
    );
  }, [currentTab, setCurrentTab, tabs, anchorEl, setModalType, setOpenHandleTabModal]);

  return (
    <div className="otc-trader-tabs-container">
      {renderTab}

      <div className="tabs-border-fill">
        <Button
          className="btn-new-tab"
          onClick={() => {
            setModalType(TypeOfView.NEW_TAB);
            setOpenHandleTabModal(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
        </Button>
      </div>

      <HandleTabModal
        handleClose={handleCloseModal}
        open={openHandleTabModal}
        saveTab={handleAddTab}
        typeOfView={modalType}
        deleteTab={handleDeleteTab}
        renameTab={handleRenameTab}
        duplicateTab={handleDuplicateTab}
      />
    </div>
  );
};
